/**
 * @FIXME(shawk):
 * Until an upstream dependency issue in "eslint-plugin-react" is resolved,
 * disabling eslint rule "react/prop-types" is the recommended approach
 * to resolve false positives in eslint.
 *
 * see:
 * - https://github.com/yannickcr/eslint-plugin-react/issues/3140
 * - https://github.com/redwoodjs/redwood/pull/3762
 */

import cx from 'classnames';
import React, { useRef, useEffect, useState } from 'react';
import composeRefs from '@seznam/compose-react-refs';
import { IconButton } from 'components/IconButton';
import { HelperText, FieldLabel } from 'components/Forms';
import { Icon } from 'components/Icon';
import { ConnectedFormInputProps } from 'types/form-field';

export type Props = {
  endAdornment?: React.ReactNode;
  enableOnClearTabIndex?: boolean;
} & ConnectedFormInputProps<HTMLInputElement>;

export const TextInput = React.forwardRef<HTMLInputElement, Props>(function TextInputWithRef(
  {
    name,
    label,
    accessibleLabel,
    hint,
    error,
    disabled,
    type = 'text',
    endAdornment,
    className,
    labelClassName,
    setError,
    setValue,
    clearValue,
    enableOnClearTabIndex = false,
    ...inputProps
  }: Props,
  ref?: React.ForwardedRef<HTMLInputElement>,
): JSX.Element {
  const inputRef = useRef<HTMLInputElement>(null);
  const [isClearable, setIsClearable] = useState<boolean>(false);

  /**
   * @NOTE(shawk): We use this approach instead of mirroring input value in
   * react state since react-hook-form controls this input and it operates in
   * an uncontrolled fashion.
   */
  useEffect(() => {
    const el = inputRef.current;

    setIsClearable(!error && !endAdornment && !!el?.value && !disabled);

    const onChange = (e: Event) => {
      const target = e.target as HTMLInputElement;
      setIsClearable(!error && !endAdornment && !!target.value);
    };

    el?.addEventListener('input', onChange);

    return () => {
      el?.removeEventListener('input', onChange);
    };
  }, [inputRef, error, endAdornment]);

  return (
    <div className={className}>
      <FieldLabel id={name} accessibleLabel={accessibleLabel} className={labelClassName} disabled={disabled}>
        {label}
      </FieldLabel>

      <div
        className={cx('relative border flex ring-inset rounded-lg min-h-[48px] sm:min-h-[56px] transition-colors', {
          'hover:bg-purple-50 bg-white hover:border-black-900 focus-within:ring-1 focus-within:border-black-900 focus-within:ring-black-900 border-black-300':
            !disabled,
          'text-gray-900': !disabled && !error,
          'bg-black-4% text-black-38% border-black-38%': disabled && !error,
          'border-error-900': !!error,
        })}
      >
        <div className="flex relative grow">
          <input
            {...inputProps}
            ref={composeRefs(inputRef, ref)}
            id={name}
            type={type}
            name={name}
            disabled={disabled}
            autoComplete="off"
            aria-invalid={error ? 'true' : 'false'}
            className={cx(
              'inset-0 w-full py-8 sm:py-12 pl-16 m-px font-medium rounded-sm-inset text-sm border-none bg-transparent leading-none outline-none placeholder:text-gray-600 font-dazzed',
              {
                'pr-16': !error,
                'pr-48': !!error,
                uppercase: type === 'date',
              },
            )}
          />

          {error && (
            <Icon
              name="Error"
              className="absolute top-0 bottom-0 right-0 mr-[13px] sm:mr-[15px] my-auto w-20 sm:w-24 text-error-900 pointer-events-none"
            />
          )}
        </div>

        {isClearable && (
          <IconButton
            tabIndex={enableOnClearTabIndex ? undefined : -1}
            size="sm"
            name="TimesCircle"
            variant="ghost"
            title="clear field"
            disabled={disabled}
            className="relative w-[34px] h-[34px] sm:w-[38px] sm:h-[38px] px-0 my-auto mx-6 sm:mx-8 min-h-0 flex-shrink-0 text-black-900"
            iconClasses="h-20 w-20 sm:h-24 sm:w-24 text-gray-600"
            onClick={() => {
              if (clearValue) {
                clearValue();
                setIsClearable(false);
              }

              inputRef.current?.focus();
            }}
          />
        )}

        {endAdornment}
      </div>
      {hint && <HelperText>{hint}</HelperText>}
      {error && <HelperText error>{error}</HelperText>}
    </div>
  );
});
